import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

const API = '/schedule-checklists';
const ACCENT = '#0f766e';

const fmtDate = (d) => {
  if (!d) return '—';
  const s = String(d).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const [y, m, day] = s.split('-');
  return `${m}/${day}/${y}`;
};

const fmtTs = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString();
  } catch {
    return String(d);
  }
};

const toInputDate = (d) => (d ? String(d).slice(0, 10) : '');

/**
 * Schedule → Checklists
 * Landing list + Financial Period (auto Mon–Sun weeks from project dates).
 */
const ScheduleChecklists = ({ jobNumber, projectId }) => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence(
    'scheduleChecklists',
    projectId || jobNumber || 'global',
    true
  );

  const [view, setView] = useState('list');
  const [catalog, setCatalog] = useState([]);
  const [rows, setRows] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [projectStart, setProjectStart] = useState('');
  const [projectFinish, setProjectFinish] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2800);
  };

  const loadCatalog = useCallback(async () => {
    if (!jobNumber) return;
    try {
      setLoading(true);
      setError(null);
      const res = await authenticatedApiCall(instance, accounts, 'GET', `${API}/${jobNumber}`);
      setCatalog(res.data.data || []);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || e.message || 'Failed to load checklists');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, jobNumber]);

  const loadFinancial = useCallback(async () => {
    if (!jobNumber) return;
    try {
      setLoading(true);
      setError(null);
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `${API}/${jobNumber}/financial-period`
      );
      const payload = res.data.data || {};
      const list = Array.isArray(payload) ? payload : (payload.rows || []);
      const cfg = Array.isArray(payload) ? null : (payload.config || null);
      setRows(list);
      setConfig(cfg);
      if (cfg) {
        setProjectStart(toInputDate(cfg.project_start));
        setProjectFinish(toInputDate(cfg.project_finish));
      }
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || e.message || 'Failed to load financial period');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, jobNumber]);

  useEffect(() => {
    if (view === 'list') loadCatalog();
    else if (view === 'financial-period') loadFinancial();
  }, [view, loadCatalog, loadFinancial]);

  const stats = useMemo(() => {
    const total = rows.length;
    const stored = rows.filter((r) => r.stored).length;
    return { total, stored, pending: total - stored };
  }, [rows]);

  const refresh = () => {
    if (view === 'list') loadCatalog();
    else loadFinancial();
  };

  const openSetup = () => {
    if (config) {
      setProjectStart(toInputDate(config.project_start));
      setProjectFinish(toInputDate(config.project_finish));
    }
    setShowSetup(true);
  };

  const generateWeeks = async () => {
    if (!projectStart || !projectFinish) {
      alert('Enter project start and finish dates');
      return;
    }
    if (projectFinish < projectStart) {
      alert('Finish must be on or after start');
      return;
    }
    if (rows.length && !window.confirm(
      'Rebuild weekly periods from these project dates?\n\nStored weeks that still match are kept. Open weeks outside the new range are removed.'
    )) {
      return;
    }
    try {
      setSaving(true);
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `${API}/${jobNumber}/financial-period/generate`,
        { project_start: projectStart, project_finish: projectFinish }
      );
      const data = res.data.data || {};
      setRows(data.rows || []);
      setConfig(data.config || { project_start: projectStart, project_finish: projectFinish });
      setShowSetup(false);
      const s = data.summary || {};
      showToast(
        `Built ${s.weeks ?? (data.rows || []).length} weeks` +
        (s.inserted != null ? ` · +${s.inserted} new` : '') +
        (s.removed ? ` · −${s.removed} removed` : '')
      );
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to generate weeks');
    } finally {
      setSaving(false);
    }
  };

  const patchRow = async (id, patch) => {
    try {
      setBusyId(id);
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'PATCH',
        `${API}/financial-period/${id}`,
        patch
      );
      setRows((prev) => prev.map((r) => (r.id === id ? res.data.data : r)));
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to update');
      await loadFinancial();
    } finally {
      setBusyId(null);
    }
  };

  const toggleStored = async (row) => {
    await patchRow(row.id, { stored: !row.stored });
    showToast(!row.stored ? 'Marked stored' : 'Cleared stored');
  };

  const deleteRow = async (row) => {
    if (!window.confirm('Delete this period week?')) return;
    try {
      setBusyId(row.id);
      await authenticatedApiCall(instance, accounts, 'DELETE', `${API}/financial-period/${row.id}`);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      showToast('Week deleted');
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to delete');
    } finally {
      setBusyId(null);
    }
  };

  const exportCsv = () => {
    const header = [
      'Start',
      'Finish',
      'Financial Period Date',
      'Stored',
      'Stored By',
      'Stored Timestamp',
    ];
    const lines = rows.map((r) =>
      [
        toInputDate(r.start_date),
        toInputDate(r.finish_date),
        toInputDate(r.financial_period_date),
        r.stored ? 'Yes' : 'No',
        r.stored_by || '',
        r.stored_timestamp ? new Date(r.stored_timestamp).toISOString() : '',
      ]
        .map((c) => `"${c}"`)
        .join(',')
    );
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${jobNumber}-financial-period-checklist.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('Exported CSV');
  };

  if (!jobNumber) {
    return <div style={{ padding: 24 }}>Select a project to open Schedule Checklists.</div>;
  }

  const setupForm = (inline = false) => (
    <div style={inline ? styles.setupInline : undefined}>
      <div style={styles.setupFields}>
        <label style={styles.field}>
          <span>Project start</span>
          <input
            type="date"
            value={projectStart}
            onChange={(e) => setProjectStart(e.target.value)}
            style={styles.fieldInput}
          />
        </label>
        <label style={styles.field}>
          <span>Project finish</span>
          <input
            type="date"
            value={projectFinish}
            onChange={(e) => setProjectFinish(e.target.value)}
            style={styles.fieldInput}
          />
        </label>
      </div>
      <p style={styles.setupHint}>
        Weeks are <strong>Monday → Sunday</strong>. Financial period date = that Monday.
      </p>
      <button
        type="button"
        style={styles.btnPrimary}
        onClick={generateWeeks}
        disabled={saving || !projectStart || !projectFinish}
      >
        {saving ? 'Building…' : rows.length ? '↻ Rebuild weeks' : '✨ Build weekly checklist'}
      </button>
    </div>
  );

  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <div style={styles.heroGlow} />
        <div style={styles.heroInner}>
          <div>
            {view !== 'list' && (
              <button type="button" onClick={() => setView('list')} style={styles.backBtn}>
                ← All checklists
              </button>
            )}
            <h2 style={styles.title}>
              {view === 'list' ? 'Checklists' : 'Financial Period'}
            </h2>
            <p style={styles.subtitle}>
              Job {jobNumber}
              {view === 'financial-period' && config
                ? ` · Project ${fmtDate(config.project_start)} → ${fmtDate(config.project_finish)} · ${stats.stored}/${stats.total} stored`
                : view === 'financial-period'
                  ? ` · ${stats.stored} of ${stats.total} stored`
                  : ' · Fillable schedule close-out lists'}
            </p>
          </div>
          <div style={styles.toolbar}>
            <ActiveViewers
              viewers={viewers}
              isConnected={isConnected}
              connectionState={connectionState}
              maxVisible={3}
              showConnectionStatus={false}
            />
            {view === 'financial-period' && (
              <>
                <button type="button" style={styles.btnPrimary} onClick={openSetup}>
                  {rows.length ? '↻ Rebuild weeks' : '✨ Set project dates'}
                </button>
                <button type="button" style={styles.btn} onClick={exportCsv} disabled={!rows.length}>
                  📤 Export
                </button>
              </>
            )}
            <button type="button" style={styles.btn} onClick={refresh} disabled={loading}>
              {loading ? '⏳' : '↻'} Refresh
            </button>
            <button type="button" style={styles.btn} onClick={() => setShowHelp(true)} title="Help">
              ❓ Help
            </button>
          </div>
        </div>
      </div>

      {toast && (
        <div style={{ ...styles.toast, background: toast.type === 'error' ? '#991b1b' : ACCENT }}>
          {toast.message}
        </div>
      )}

      {error && <div style={styles.error}>{error}</div>}

      {view === 'list' && (
        <div style={styles.cardGrid}>
          {loading && !catalog.length && (
            <div style={styles.empty}>Loading checklists…</div>
          )}
          {catalog.map((c) => {
            const pct = c.total ? Math.round((c.completed / c.total) * 100) : 0;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setView(c.id === 'financial-period' ? 'financial-period' : 'list')}
                style={styles.card}
              >
                <div style={{ ...styles.cardIcon, background: c.accent || ACCENT }}>{c.icon || '📋'}</div>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={styles.cardTitle}>{c.name}</div>
                  <div style={styles.cardDesc}>{c.description}</div>
                  <div style={styles.progressTrack}>
                    <div style={{ ...styles.progressFill, width: `${pct}%`, background: c.accent || ACCENT }} />
                  </div>
                  <div style={styles.cardMeta}>
                    {c.total === 0
                      ? 'Set project start & finish to build weeks'
                      : `${c.completed} stored · ${c.pending} open · ${c.total} weeks`}
                  </div>
                </div>
                <span style={styles.cardChevron}>›</span>
              </button>
            );
          })}
        </div>
      )}

      {view === 'financial-period' && (
        <>
          {rows.length > 0 && (
            <div style={styles.statRow}>
              <div style={styles.stat}>
                <div style={styles.statNum}>{stats.total}</div>
                <div style={styles.statLabel}>Weeks</div>
              </div>
              <div style={{ ...styles.stat, borderColor: '#a7f3d0' }}>
                <div style={{ ...styles.statNum, color: '#047857' }}>{stats.stored}</div>
                <div style={styles.statLabel}>Stored</div>
              </div>
              <div style={{ ...styles.stat, borderColor: '#fde68a' }}>
                <div style={{ ...styles.statNum, color: '#b45309' }}>{stats.pending}</div>
                <div style={styles.statLabel}>Open</div>
              </div>
            </div>
          )}

          {loading && !rows.length && !config ? (
            <div style={styles.empty}>Loading financial period…</div>
          ) : rows.length === 0 ? (
            <div style={styles.emptyBox}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>💰</div>
              <div style={{ fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                Set the project dates
              </div>
              <div style={{ fontSize: 13, color: '#64748b', maxWidth: 440, margin: '0 auto 18px', lineHeight: 1.5 }}>
                Enter when the job starts and finishes. The portal builds every
                Monday–Sunday week in between. Financial period date is the Monday.
              </div>
              {setupForm(true)}
            </div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Start (Mon)</th>
                    <th style={styles.th}>Finish (Sun)</th>
                    <th style={styles.th}>Financial Period Date</th>
                    <th style={{ ...styles.th, textAlign: 'center', width: 90 }}>Stored</th>
                    <th style={styles.th}>Stored By</th>
                    <th style={styles.th}>Stored Timestamp</th>
                    <th style={{ ...styles.th, width: 56 }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      style={{
                        ...styles.tr,
                        background: row.stored ? '#f0fdf4' : '#fff',
                        opacity: busyId === row.id ? 0.65 : 1,
                      }}
                    >
                      <td style={styles.td}>{fmtDate(row.start_date)}</td>
                      <td style={styles.td}>{fmtDate(row.finish_date)}</td>
                      <td style={{ ...styles.td, fontWeight: 600 }}>{fmtDate(row.financial_period_date)}</td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => toggleStored(row)}
                          title={row.stored ? 'Clear stored' : 'Mark stored'}
                          style={{
                            ...styles.checkBtn,
                            background: row.stored ? '#059669' : '#fff',
                            borderColor: row.stored ? '#059669' : '#cbd5e1',
                            color: row.stored ? '#fff' : '#94a3b8',
                          }}
                        >
                          {row.stored ? '✓' : ''}
                        </button>
                      </td>
                      <td style={{ ...styles.td, color: '#475569', fontSize: 13 }}>
                        {row.stored_by || '—'}
                      </td>
                      <td style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>
                        {fmtTs(row.stored_timestamp)}
                      </td>
                      <td style={styles.td}>
                        <button
                          type="button"
                          onClick={() => deleteRow(row)}
                          style={styles.iconBtn}
                          title="Delete"
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={styles.tableFoot}>
                Tick <strong>Stored</strong> when that week’s period is booked — stamps you and the time automatically.
              </div>
            </div>
          )}
        </>
      )}

      {showSetup && rows.length > 0 && (
        <div style={styles.modalBackdrop} onClick={() => !saving && setShowSetup(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <strong>Rebuild from project dates</strong>
              <button type="button" style={styles.closeX} onClick={() => setShowSetup(false)}>×</button>
            </div>
            <div style={styles.modalBody}>
              {setupForm(false)}
            </div>
          </div>
        </div>
      )}

      {showHelp && (
        <div style={styles.modalBackdrop} onClick={() => setShowHelp(false)}>
          <div style={{ ...styles.modal, maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ ...styles.modalHeader, background: ACCENT, color: '#fff', borderBottom: 'none' }}>
              <strong style={{ flex: 1 }}>❓ Schedule Checklists — Help</strong>
              <ListenButton
                compact
                guideId="schedule-checklists-help"
                title="Schedule Checklists Help"
                accent={ACCENT}
                dark
              />
              <button type="button" onClick={() => setShowHelp(false)} style={{ ...styles.closeX, color: '#fff' }}>
                ×
              </button>
            </div>
            <div style={{ ...styles.modalBody, lineHeight: 1.55, color: '#334155', fontSize: 14 }}>
              <HelpWithVideoTour guideId="schedule-checklists-help" title="Schedule Checklists Help" accent="#0f766e">
              <p>
                <strong>Financial Period.</strong> Enter only the project start and finish.
                The portal builds every Monday–Sunday week that covers the job.
                The financial period date is always that week’s Monday.
              </p>
              <p>
                <strong>Stored.</strong> Tick when the period is booked. Stored By and Stored Timestamp
                fill in automatically. Rebuild weeks keeps already-stored Mondays that still fall in range.
              </p>
              <p style={{ marginBottom: 0 }}>
                Use Export for a CSV, Refresh to reload, and Help anytime for this guide.
              </p>
              </HelpWithVideoTour>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100%',
    background: 'linear-gradient(180deg, #f0fdfa 0%, #f8fafc 180px, #f8fafc 100%)',
    padding: '0 0 40px',
  },
  hero: {
    position: 'relative',
    overflow: 'hidden',
    borderBottom: '1px solid #d1fae5',
    marginBottom: 20,
  },
  heroGlow: {
    position: 'absolute',
    inset: 0,
    background:
      'radial-gradient(ellipse 60% 120% at 10% -20%, rgba(15,118,110,0.18), transparent 55%), radial-gradient(ellipse 40% 80% at 90% 0%, rgba(20,184,166,0.12), transparent 50%)',
    pointerEvents: 'none',
  },
  heroInner: {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
    padding: '20px 24px 18px',
  },
  backBtn: {
    border: 'none',
    background: 'transparent',
    color: ACCENT,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
    marginBottom: 4,
  },
  title: {
    margin: 0,
    fontSize: '1.45rem',
    fontWeight: 700,
    color: '#0f172a',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    margin: '4px 0 0',
    color: '#64748b',
    fontSize: 13,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  btn: {
    border: '1px solid #cbd5e1',
    background: '#fff',
    borderRadius: 8,
    padding: '7px 12px',
    fontSize: 13,
    cursor: 'pointer',
    color: '#334155',
  },
  btnPrimary: {
    border: 'none',
    background: ACCENT,
    color: '#fff',
    borderRadius: 8,
    padding: '7px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  toast: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    color: '#fff',
    padding: '10px 16px',
    borderRadius: 10,
    zIndex: 11000,
    fontSize: 13,
    boxShadow: '0 8px 24px rgba(15,23,42,0.18)',
  },
  error: {
    margin: '0 24px 16px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#991b1b',
    padding: '10px 14px',
    borderRadius: 8,
  },
  cardGrid: {
    display: 'grid',
    gap: 12,
    padding: '0 24px',
    maxWidth: 920,
  },
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '18px 20px',
    border: '1px solid #e2e8f0',
    borderRadius: 14,
    background: '#fff',
    cursor: 'pointer',
    textAlign: 'left',
    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
  },
  cardIcon: {
    width: 52,
    height: 52,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 24,
    flexShrink: 0,
    color: '#fff',
  },
  cardTitle: {
    fontWeight: 700,
    color: '#0f172a',
    fontSize: 16,
    marginBottom: 4,
  },
  cardDesc: {
    fontSize: 13,
    color: '#64748b',
    marginBottom: 10,
    lineHeight: 1.4,
  },
  progressTrack: {
    height: 6,
    borderRadius: 999,
    background: '#e2e8f0',
    overflow: 'hidden',
    marginBottom: 6,
  },
  progressFill: {
    height: '100%',
    borderRadius: 999,
  },
  cardMeta: {
    fontSize: 12,
    color: '#94a3b8',
  },
  cardChevron: {
    fontSize: 28,
    color: '#cbd5e1',
    fontWeight: 300,
    lineHeight: 1,
  },
  empty: {
    color: '#64748b',
    padding: '40px 24px',
    textAlign: 'center',
  },
  emptyBox: {
    margin: '0 24px',
    border: '1px dashed #cbd5e1',
    borderRadius: 14,
    padding: '36px 28px',
    textAlign: 'center',
    background: '#fff',
    maxWidth: 520,
  },
  setupInline: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12,
    textAlign: 'left',
  },
  setupFields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  setupHint: {
    margin: 0,
    fontSize: 12,
    color: '#64748b',
    lineHeight: 1.4,
  },
  statRow: {
    display: 'flex',
    gap: 10,
    padding: '0 24px 16px',
    flexWrap: 'wrap',
  },
  stat: {
    minWidth: 100,
    padding: '12px 16px',
    borderRadius: 12,
    background: '#fff',
    border: '1px solid #e2e8f0',
  },
  statNum: {
    fontSize: 22,
    fontWeight: 700,
    color: '#0f172a',
    lineHeight: 1.1,
  },
  statLabel: {
    fontSize: 12,
    color: '#94a3b8',
    marginTop: 2,
  },
  tableWrap: {
    margin: '0 24px',
    border: '1px solid #e2e8f0',
    borderRadius: 14,
    overflow: 'hidden',
    background: '#fff',
    boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '11px 12px',
    fontSize: 12,
    fontWeight: 700,
    color: '#475569',
    background: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    whiteSpace: 'nowrap',
  },
  tr: {
    borderBottom: '1px solid #f1f5f9',
  },
  td: {
    padding: '10px 12px',
    verticalAlign: 'middle',
    fontSize: 13,
    color: '#0f172a',
  },
  checkBtn: {
    width: 28,
    height: 28,
    borderRadius: 8,
    border: '2px solid',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 1,
  },
  iconBtn: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 15,
    opacity: 0.55,
  },
  tableFoot: {
    padding: '10px 14px',
    fontSize: 12,
    color: '#94a3b8',
    background: '#fafafa',
    borderTop: '1px solid #f1f5f9',
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.45)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  modal: {
    background: '#fff',
    borderRadius: 12,
    maxWidth: 440,
    width: '100%',
    maxHeight: '85vh',
    overflow: 'auto',
    boxShadow: '0 20px 50px rgba(15,23,42,0.25)',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '14px 16px',
    borderBottom: '1px solid #e2e8f0',
  },
  modalBody: {
    padding: 18,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  closeX: {
    background: 'none',
    border: 'none',
    fontSize: 22,
    cursor: 'pointer',
    color: '#64748b',
    lineHeight: 1,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 13,
    color: '#475569',
    fontWeight: 600,
  },
  fieldInput: {
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    padding: '8px 10px',
    fontSize: 14,
    fontWeight: 400,
    color: '#0f172a',
  },
};

export default ScheduleChecklists;
